import type { CategoryLeaf, CategoryTreeNode } from '@repo/api-contracts';
import { GripVertical } from 'lucide-react';
import type { ReactElement } from 'react';
import { beginMoveDrag, type RenameController } from '@/shared/lib';
import { Button, cn, tableStyles } from '@/shared/ui';
import type { DragReorderController } from '../model/drag-reorder-controller';
import { CategoryNameCell } from './category-name-cell';

interface CategoryRootRowProps {
  readonly root: CategoryTreeNode;
  readonly index: number;
  readonly isExpanded: boolean;
  readonly drag: DragReorderController;
  readonly rename: RenameController;
  readonly onRenameStart: (category: CategoryLeaf) => void;
  readonly onToggleExpand: (root: CategoryTreeNode) => void;
  readonly onDelete: (root: CategoryTreeNode) => void;
}

/** 대분류 표의 한 행 — 드래그 순서 변경·이름 수정·소분류 관리·삭제. */
export const CategoryRootRow = ({
  root,
  index,
  isExpanded,
  drag,
  rename,
  onRenameStart,
  onToggleExpand,
  onDelete,
}: CategoryRootRowProps): ReactElement => (
  <tr
    draggable={rename.target?.id !== root.id}
    onDragStart={(event) => {
      if (beginMoveDrag(event, root.id)) {
        drag.onDragStart(index);
      }
    }}
    onDragOver={(event) => event.preventDefault()}
    onDrop={() => drag.onDrop(index)}
    onDragEnd={drag.onDragEnd}
    className={cn(tableStyles.row, 'cursor-grab', drag.draggingIndex === index && 'opacity-40')}
  >
    <td className={tableStyles.td}>
      <span className="flex items-center gap-2">
        <GripVertical className="size-4 text-muted-foreground" aria-hidden="true" />
        {index + 1}
      </span>
    </td>
    <td className={cn(tableStyles.td, 'font-medium')}>
      <CategoryNameCell category={root} rename={rename} />
    </td>
    <td className={cn(tableStyles.td, 'text-muted-foreground')}>
      {root.children.length > 0 ? root.children.map((child) => child.name).join(' · ') : '—'}
    </td>
    <td className={tableStyles.td}>
      <span className="flex items-center gap-1">
        {rename.target?.id === root.id ? null : (
          <Button variant="ghost" size="sm" onClick={() => onRenameStart(root)}>
            수정
          </Button>
        )}
        <Button
          variant="ghost"
          size="sm"
          aria-expanded={isExpanded}
          aria-label={`${root.name} 소분류 관리`}
          onClick={() => onToggleExpand(root)}
        >
          {isExpanded ? '소분류 닫기' : '소분류 관리'}
        </Button>
        <Button
          variant="ghost"
          size="sm"
          className="text-destructive"
          aria-label={`${root.name} 삭제`}
          onClick={() => onDelete(root)}
        >
          삭제
        </Button>
      </span>
    </td>
  </tr>
);
